import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Colonnes redimensionnables à la souris.
 *
 * ── Pourquoi c'est nécessaire ───────────────────────────────────────────────
 * Les tableaux du cahier journal, de la programmation et des progressions
 * portent du texte libre dont la longueur varie du tout au tout : un
 * déroulement de séance fait dix lignes, un intitulé de matière trois mots. Une
 * largeur figée condamne l'un ou l'autre — soit le déroulement défile dans un
 * timbre-poste, soit la matière occupe le quart de l'écran pour rien.
 *
 * ── Les largeurs SURVIVENT au rechargement ──────────────────────────────────
 * Régler ses colonnes est un travail d'installation, pas un geste quotidien.
 * Le perdre à chaque visite reviendrait à ne pas offrir le réglage.
 *
 * ── Une poignée, pas la bordure entière ─────────────────────────────────────
 * La zone de saisie occupe toute la cellule. Rendre la bordure elle-même
 * saisissable ferait attraper une colonne en visant la fin d'un mot ; la
 * poignée est étroite et se signale au survol.
 */

/** Largeur minimale, en pixels : en deçà, l'en-tête devient illisible. */
const LARGEUR_MINIMALE = 60;

export interface ColonnesRedimensionnables {
  /** Largeurs courantes, indexées par clé de colonne. `null` = automatique. */
  largeurs: Record<string, number>;
  /** À rendre dans un `<colgroup>` pour appliquer les largeurs. */
  colgroup: (cles: string[]) => React.ReactElement;
  /** À poser dans chaque `<th>` redimensionnable. */
  poignee: (cle: string) => React.ReactElement;
  /**
   * Style à poser sur le `<th>` de la colonne. **Indispensable.**
   *
   * ── Ce qui ne marchait pas ────────────────────────────────────────────────
   * Le réglage passait uniquement par `<col style="width">`. Or, avec
   * `table-layout: auto` — la valeur par défaut, et celle de tous nos
   * tableaux — la largeur d'un `<col>` n'est qu'une SUGGESTION : le navigateur
   * répartit selon le contenu et selon les contraintes des cellules. Nos
   * en-têtes portant `min-w-[13rem]`, aucune colonne ne pouvait descendre sous
   * 208 px, et l'élargissement se perdait dans la répartition.
   *
   * Mesuré avant correction : glisser la poignée enregistrait bien 408 px dans
   * le stockage local et posait `<col style="width:408px">` — et la colonne
   * restait à 208. La poignée s'attrapait, se déplaçait, et ne faisait rien.
   *
   * Fixer les trois bornes sur la cellule d'en-tête lève l'ambiguïté : une
   * cellule dont `width`, `min-width` et `max-width` coïncident n'a plus de
   * marge de manœuvre, même en `table-layout: auto`. Les colonnes non réglées
   * gardent leur comportement d'origine.
   */
  entete: (cle: string) => { style?: React.CSSProperties };
}

export function useColonnes(identifiant: string): ColonnesRedimensionnables {
  const cle = `journal.colonnes.${identifiant}`;

  const [largeurs, setLargeurs] = useState<Record<string, number>>(() => {
    try {
      const brut = globalThis.localStorage?.getItem(cle);
      return brut ? (JSON.parse(brut) as Record<string, number>) : {};
    } catch {
      // Un réglage illisible ne doit pas empêcher l'écran de s'ouvrir : on
      // repart des largeurs automatiques.
      return {};
    }
  });

  /*
   * Le glissement en cours vit dans une référence, pas dans un état.
   *
   * Chaque pixel parcouru déclencherait sinon un rendu de tout le tableau —
   * plusieurs centaines de cellules pour une grille d'évaluation. Seule la
   * largeur finale est enregistrée.
   */
  const glissement = useRef<{
    cle: string;
    departX: number;
    departLargeur: number;
    /** Rapport entre pixels affichés et pixels de mise en page. Voir `demarrer`. */
    echelle: number;
  } | null>(null);

  useEffect(() => {
    globalThis.localStorage?.setItem(cle, JSON.stringify(largeurs));
  }, [cle, largeurs]);

  const demarrer = useCallback((colonne: string, evenement: React.PointerEvent) => {
    const th = (evenement.currentTarget as HTMLElement).closest('th');
    if (!th) return;
    evenement.preventDefault();
    /*
     * La capture du pointeur permet de continuer à suivre la souris au-delà de
     * la poignée, large de quelques pixels. Elle est utile, pas indispensable :
     * là où elle manque, le glissement fonctionne encore tant que le curseur
     * reste dans le tableau. On ne laisse donc pas son absence interrompre le
     * redimensionnement.
     */
    try {
      (evenement.currentTarget as HTMLElement).setPointerCapture(evenement.pointerId);
    } catch { /* pas de capture disponible : on glisse sans */ }
    /*
     * ── Deux unités qu'il ne faut pas confondre ─────────────────────────────
     *
     * `clientX` et `getBoundingClientRect()` rendent des pixels TELS
     * QU'AFFICHÉS ; `offsetWidth` rend des pixels de MISE EN PAGE. Sous le
     * `zoom` CSS que l'application applique à son contenu, les deux diffèrent
     * du facteur de zoom — mesuré : une colonne de 200 px en mesure 320 à
     * 160 %.
     *
     * Or la largeur calculée ici repart en `style.width`, donc en mise en
     * page. Partir du rectangle affiché faisait bondir la colonne au facteur
     * de zoom dès le premier pixel parcouru, puis la faisait glisser plus vite
     * que le curseur — la poignée fuyait la souris. À 100 % le défaut
     * n'existait pas, ce qui explique qu'il ait tenu.
     *
     * L'échelle se déduit de l'élément lui-même, ses deux mesures rapportées
     * l'une à l'autre : une seule source, et elle suit toute mise à l'échelle,
     * d'où qu'elle vienne.
     */
    const affichee = th.getBoundingClientRect().width;
    glissement.current = {
      cle: colonne,
      departX: evenement.clientX,
      departLargeur: th.offsetWidth,
      echelle: th.offsetWidth > 0 ? affichee / th.offsetWidth : 1,
    };
  }, []);

  const deplacer = useCallback((evenement: React.PointerEvent) => {
    const en_cours = glissement.current;
    if (!en_cours) return;
    const largeur = Math.max(
      LARGEUR_MINIMALE,
      // Le déplacement du curseur est mesuré à l'écran ; la largeur s'écrit en
      // mise en page. L'un ne devient l'autre qu'en divisant par l'échelle.
      en_cours.departLargeur + (evenement.clientX - en_cours.departX) / en_cours.echelle,
    );
    setLargeurs((actuelles) => ({ ...actuelles, [en_cours.cle]: Math.round(largeur) }));
  }, []);

  const terminer = useCallback(() => { glissement.current = null; }, []);

  const colgroup = useCallback((cles: string[]) => (
    <colgroup>
      {cles.map((c) => (
        <col key={c} {...(largeurs[c] ? { style: { width: `${largeurs[c]}px` } } : {})} />
      ))}
    </colgroup>
  ), [largeurs]);

  const entete = useCallback((colonne: string) => {
    const largeur = largeurs[colonne];
    if (!largeur) return {};
    return {
      style: {
        width: `${largeur}px`,
        minWidth: `${largeur}px`,
        maxWidth: `${largeur}px`,
      } as React.CSSProperties,
    };
  }, [largeurs]);

  const poignee = useCallback((colonne: string) => (
    <span
      /*
       * `aria-hidden` : la poignée sort de l'arbre d'accessibilité.
       *
       * Deux raisons. D'abord elle s'agrégeait au nom de l'en-tête —
       * « Horaire » devenait « Horaire Redimensionner la colonne Horaire »,
       * ce qui rend la lecture d'une ligne d'en-têtes pénible. Ensuite elle
       * n'est utilisable qu'au pointeur : glisser et double-cliquer n'ont pas
       * d'équivalent clavier, et l'annoncer promettrait une action
       * inatteignable.
       *
       * Rien n'est perdu pour autant : la largeur des colonnes est un confort
       * d'affichage, jamais une condition d'accès au contenu.
       */
      aria-hidden="true"
      onPointerDown={(e) => demarrer(colonne, e)}
      onPointerMove={deplacer}
      onPointerUp={terminer}
      onPointerCancel={terminer}
      // Double-clic : retour à la largeur automatique. C'est l'issue quand on
      // a réduit une colonne au point de ne plus pouvoir l'attraper.
      onDoubleClick={() => setLargeurs((a) => {
        const suivant = { ...a };
        delete suivant[colonne];
        return suivant;
      })}
      className={[
        // `poignee-colonne` : le document d'impression la retire par ce nom.
        // Il se fiait auparavant à `aria-hidden`, qui marque bien d'autres
        // choses — dont la colonne des heures de l'emploi du temps.
        'poignee-colonne',
        'absolute inset-y-0 right-0 w-1.5 cursor-col-resize select-none',
        'touch-none hover:bg-[var(--color-primary)]/40',
        'print:hidden',
      ].join(' ')}
    />
  ), [demarrer, deplacer, terminer]);

  return { largeurs, colgroup, poignee, entete };
}
